import React from 'react';
import { Seo } from '@/components/Seo';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  Database,
  ExternalLink,
  BarChart,
  Info,
  ArrowLeft,
  ArrowRight,
  Droplets,
  Waves,
  Trash2,
  Home,
  Flame,
  MapPin,
  LineChart,
  Share2,
  Mail,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const FEE_TYPES = [
  {
    id: 'vann',
    name: 'Vanngebyr',
    desc: 'Tilgang til rent drikkevann og vannforsyning til boligen.',
    icon: Droplets,
    iconCls: 'text-nordicBlue',
  },
  {
    id: 'avløp',
    name: 'Avløpsgebyr',
    desc: 'Håndtering av avløpsvann (kloakk og regnvann).',
    icon: Waves,
    iconCls: 'text-nordicBlue',
  },
  {
    id: 'renovasjon',
    name: 'Renovasjonsgebyr',
    desc: 'Innsamling, transport og behandling av husholdningsavfall.',
    icon: Trash2,
    iconCls: 'text-nordicBlue',
  },
  {
    id: 'eiendom',
    name: 'Septiktømming',
    desc: 'Tomming av septiktank for husstander uten kloakk. (Ikke valgt som standard i avgiftsfilteret, men kan velges til.)',
    icon: Home,
    iconCls: 'text-nordicBlue',
  },
  {
    id: 'feiing',
    name: 'Feiing og tilsyn',
    desc: 'Feiing og tilsyn med ildsteder og skorsteiner.',
    icon: Flame,
    iconCls: 'text-nordicBlue',
  },
];

const STEPS: Array<{
  title: string;
  icon: typeof MapPin;
  desc: string;
  links: Array<{ to: string; label: string }>;
}> = [
  {
    title: 'Utforsk kommuner',
    icon: MapPin,
    desc: 'På forsiden kan du søke, filtrere på fylke og sortere. Kartvisningen viser alle kommuner med farge etter avgiftsnivå. Klikk på en kommune for detaljer, fordeling per avgiftstype, historikk og plassering i Norge.',
    links: [{ to: '/', label: 'Gå til forsiden' }, { to: '/kart', label: 'Åpne kartet' }],
  },
  {
    title: 'Sammenlign kommuner',
    icon: LineChart,
    desc: 'Velg opptil 5 kommuner og se stolpediagram, historisk utvikling over tid, og radar-diagram som sammenligner hver avgiftstype. Du kan også se fylkesgjennomsnitt og hvordan kommunen avviker.',
    links: [{ to: '/sammenlign', label: 'Start sammenligning' }],
  },
  {
    title: 'Del og lag widgeter',
    icon: Share2,
    desc: 'Lag tilpassede widgeter: nøkkeltall, kommunesammenligning, fylkesoversikt, toppliste, enkeltbadge, avgiftsfordeling med mer. Velg år, fylker, kommuner, tema og bakgrunnsfarge. Kopier iframe-kode for å sette inn på blogg eller nettside.',
    links: [{ to: '/del', label: 'Lag widget' }],
  },
];

const AboutPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-softWhite">
      <Seo
        title="Om prosjektet | Kommunale avgifter i Norge"
        description="Lær mer om prosjektet Kommunale avgifter: datakilder fra SSB, hvordan avgiftene beregnes for en standardbolig på 120m², og hvordan du kan bruke dataene."
        path="/om"
      />
      <Header />

      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Hero – samme stil som kommune-detalj */}
        <div className="mb-8 py-6 px-5 md:px-6 rounded-2xl bg-gradient-to-br from-nordicBlue/5 to-transparent border border-nordicBlue/10">
          <nav className="flex items-center gap-2 text-sm text-warmGray mb-4">
            <Link to="/" className="flex items-center gap-1.5 hover:text-nordicBlue transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" />
            Forside
          </Link>
            <span>/</span>
            <span className="text-nordicBlue font-semibold">Om prosjektet</span>
          </nav>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-deepCharcoal mb-2">
              Om Kommunale avgifter
            </h1>
            <p className="text-warmGray">
              En åpen informasjonsportal som tilgjengeliggjør data om kommunale avgifter 
              for norske husstander. Data fra SSB.
            </p>
          </div>
        </div>

        {/* Hovedtall-kort – samme stil som forsiden */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="rounded-xl bg-slate-50 p-3 shrink-0">
              <MapPin className="h-6 w-6 text-slate-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-warmGray uppercase tracking-wide">Kommuner</p>
              <p className="text-base font-semibold text-deepCharcoal">350+</p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="rounded-xl bg-slate-50 p-3 shrink-0">
              <BarChart className="h-6 w-6 text-slate-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-warmGray uppercase tracking-wide">Avgiftstyper</p>
              <p className="text-base font-semibold text-deepCharcoal">5</p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="rounded-xl bg-slate-50 p-3 shrink-0">
              <Database className="h-6 w-6 text-slate-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-warmGray uppercase tracking-wide">Datakilde</p>
              <p className="text-base font-semibold text-deepCharcoal">SSB</p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="rounded-xl bg-slate-50 p-3 shrink-0">
              <Home className="h-6 w-6 text-slate-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-warmGray uppercase tracking-wide">Standardbolig</p>
              <p className="text-base font-semibold text-deepCharcoal">120 m²</p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* Hva er kommunale avgifter */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-100 p-4 md:p-6">
            <h2 className="text-xl font-bold text-deepCharcoal mb-4 flex items-center gap-2">
              <BarChart className="h-5 w-5 text-nordicBlue" />
              Hva er kommunale avgifter?
            </h2>
            <p className="text-warmGray mb-5">
              Kommunale avgifter er gebyrer som kommunene krever inn for å finansiere 
              lokale tjenester. De viktigste for husstander:
            </p>
            <ul className="space-y-3">
              {FEE_TYPES.map((fee) => {
                const Icon = fee.icon;
                return (
                  <li key={fee.id} className="flex gap-3">
                    <span className="shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-nordicBlue/5 flex items-center justify-center">
                      <Icon className={`h-4 w-4 ${fee.iconCls}`} />
                    </span>
                    <div>
                      <span className="font-medium text-deepCharcoal">{fee.name}</span>
                      <span className="text-warmGray"> — {fee.desc}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Slik bruker du tjenesten */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-100 p-4 md:p-6">
            <h2 className="text-xl font-bold text-deepCharcoal mb-4">Slik bruker du tjenesten</h2>
            <div className="space-y-6">
              {STEPS.map((step, i) => {
                const Icon = step.icon;
                const stepLinks = step.links;
                return (
                  <div key={i} className="flex gap-4">
                    <span className="shrink-0 w-8 h-8 rounded-full bg-nordicBlue/10 flex items-center justify-center text-sm font-bold text-nordicBlue">
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <h3 className="font-semibold text-deepCharcoal mb-2">{step.title}</h3>
                      <p className="text-sm text-warmGray mb-3">{step.desc}</p>
                      {stepLinks.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {stepLinks.map((l) => (
                            <Button key={l.to} asChild variant="outline" size="sm">
                              <Link to={l.to} className="flex items-center gap-1">
                                {l.label}
                                <ArrowRight className="h-3 w-3" />
                              </Link>
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Om prosjektet + Datakilder + Kontakt – 3 kolonner på stor skjerm */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-slate-100 p-4 md:p-6">
              <h2 className="text-xl font-bold text-deepCharcoal mb-4 flex items-center gap-2">
                <Info className="h-5 w-5 text-nordicBlue" />
                Om prosjektet
              </h2>
              <p className="text-warmGray mb-4">
                «Kommunale avgifter» er en åpen informasjonsportal som tilgjengeliggjør data om 
                kommunale avgifter for norske husstander. Formålet er å gi innbyggere, forskere 
                og beslutningstakere enkel tilgang til denne informasjonen.
              </p>
              <p className="text-warmGray mb-4">
                Prosjektet ble startet i 2025 for å skape åpenhet rundt kommunale avgifter 
                og gjøre det lettere å sammenligne avgiftsnivået på tvers av kommuner.
              </p>
              <Alert className="border-slate-200 bg-slate-50 text-warmGray">
                <AlertTitle className="text-deepCharcoal">Viktig</AlertTitle>
                <AlertDescription>
                  Tjenesten er utviklet av{' '}
                  <a href="https://roste-consulting.no" target="_blank" rel="noopener noreferrer" className="text-nordicBlue hover:underline">
                    Roste Consulting
                  </a>{' '}
                  og er ikke en offentlig tjeneste. Dobbelsjekk tall mot offisielle kilder.
                </AlertDescription>
              </Alert>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-100 p-4 md:p-6">
              <h2 className="text-xl font-bold text-deepCharcoal mb-4 flex items-center gap-2">
                <Database className="h-5 w-5 text-nordicBlue" />
                Datakilder
              </h2>
              <p className="text-warmGray mb-4">
                Alle data er hentet fra Statistisk sentralbyrå (SSB), tabell 12842 
                «Kommunale gebyrer knyttet til bolig». Vi bruker en standardbolig på <strong>120 m²</strong>.
              </p>
              <a
                href="https://www.ssb.no/statbank/table/12842/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-nordicBlue font-medium hover:underline"
              >
                Se datakilden hos SSB
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-slate-100 p-4 md:p-6">
              <h2 className="text-xl font-bold text-deepCharcoal mb-4 flex items-center gap-2">
                <Mail className="h-5 w-5 text-nordicBlue" />
                Kontakt
              </h2>
              <p className="text-warmGray mb-3">
                Spørsmål eller tilbakemeldinger?
              </p>
              <a href="mailto:kontakt@kommunaleavgifter.no" className="text-nordicBlue font-medium hover:underline">
                kontakt@kommunaleavgifter.no
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage; 